/**
 * MobilePlayLayout — phone layout for the in-match Play route.
 *
 * Tuned for the simplified-play loop: the player only ever drives
 * `execute-tests` interactively. Everything else (deploy, document
 * cards, fortify) is handled by the auto-pilot in `routes/Play.tsx`.
 *
 * Surfaces:
 *
 *   ┌─────────────────────────┐
 *   │ MobileStatusBar    44px │
 *   ├─────────────────────────┤
 *   │ PhaseCoach         40px │
 *   ├─────────────────────────┤
 *   │                         │
 *   │   MapView (full bleed)  │  pinch + pan via TransformWrapper
 *   │                         │
 *   ├─────────────────────────┤
 *   │ MobileActionBar    88px │  ALWAYS visible (state-aware content)
 *   └─────────────────────────┘
 *
 * The action bar's content adapts to attackState:
 *   - not my turn:    "WAITING FOR {name}"        (passive)
 *   - idle, my turn:  big "END TURN" button       (give the player an out)
 *   - sourceSelected: "{src} → tap an enemy" + Cancel
 *   - targetSelected: "{src} → {tgt}"  + Attack 1/2/3 + Cancel
 *
 * During combat-modal kinds (combatActive / combatResolved /
 * territoryConquered) the bar is hidden so the modal owns the screen.
 */

import { type ReactNode } from 'react';
import type {
  AttackState,
  GameState,
  Player,
  PlayerId,
} from '@qd/protocol';
import type { PlayerCount, TerritoryId } from '@qd/map';
import { surfaces, fontStacks } from '@qd/design-tokens';
import { MapView } from '../../map/MapView';
import { PhaseCoach, PHASE_COACH_HEIGHT_MOBILE } from '../PhaseCoach';
import { MobileStatusBar, MOBILE_STATUS_BAR_HEIGHT } from './MobileStatusBar';
import { MobilePlayerRoster } from './MobilePlayerRoster';

const MOBILE_ACTION_BAR_HEIGHT = 88;

interface MobilePlayLayoutProps {
  readonly gameState: GameState;
  readonly selfId: PlayerId | null;
  readonly currentPlayer: Player | null;
  readonly attackState: AttackState;
  readonly sourceName: string;
  readonly targetName: string;
  readonly playerCount: PlayerCount;
  readonly sessionId: string;
  readonly isMyTurn: boolean;
  readonly turnTimerSeconds: number;
  readonly onSelectSource: (id: TerritoryId) => void;
  readonly onSelectTarget: (id: TerritoryId) => void;
  readonly onCommitAttack: (cubes: 1 | 2 | 3) => void;
  readonly onEndCombat: () => void;
  readonly onEndPhase: () => void;
  /**
   * True when the self player is in execute-tests with idle attack
   * state, has no legal attack from any territory, and HAS reserves.
   * Activates rescue-deploy click semantics on the map and renders the
   * RescueDeployState in the action bar.
   */
  readonly inRescueDeployMode?: boolean;
  /**
   * True when the self player is in execute-tests, idle, has no legal
   * attack, and has zero reserves. Renders the HardStuckState which
   * surfaces End Turn as the only path forward.
   */
  readonly isHardStuck?: boolean;
  /** Self player's reserveCubes; shown in the rescue-deploy bar. */
  readonly reserveCubes?: number;
  /** Fires when the player taps an owned territory in rescue-deploy mode. */
  readonly onRescueDeploy?: (id: TerritoryId) => void;
}

export function MobilePlayLayout({
  gameState,
  selfId,
  currentPlayer,
  attackState,
  sourceName,
  targetName,
  playerCount,
  sessionId,
  isMyTurn,
  turnTimerSeconds,
  onSelectSource,
  onSelectTarget,
  onCommitAttack,
  onEndCombat,
  onEndPhase,
  inRescueDeployMode = false,
  isHardStuck = false,
  reserveCubes = 0,
  onRescueDeploy,
}: MobilePlayLayoutProps): ReactNode {
  const topOffset = MOBILE_STATUS_BAR_HEIGHT + PHASE_COACH_HEIGHT_MOBILE;
  // Hide the action bar only during combat-modal kinds; the modal
  // takes over the screen and the bar would either fight it for
  // attention or look stranded behind the backdrop.
  const ak = attackState.kind;
  const inCombatModal =
    ak === 'combatActive' || ak === 'combatResolved' || ak === 'territoryConquered';
  const showActionBar = !inCombatModal;
  const bottomOffset = showActionBar ? MOBILE_ACTION_BAR_HEIGHT : 0;

  return (
    <>
      <MobileStatusBar
        roundNumber={gameState.roundNumber}
        turnNumber={gameState.turnNumber}
        currentPlayerIdx={gameState.currentPlayerIdx}
        currentPlayer={currentPlayer}
        sessionId={sessionId}
        turnTimerSeconds={turnTimerSeconds}
      />
      <PhaseCoach
        isMyTurn={isMyTurn}
        currentPlayerName={currentPlayer?.name ?? null}
        phase={gameState.phase}
        attackState={attackState}
        sourceName={sourceName}
        targetName={targetName}
        dashboard={null}
        reserveCubes={reserveCubes}
        topOffsetPx={MOBILE_STATUS_BAR_HEIGHT}
        inRescueDeployMode={inRescueDeployMode}
        isHardStuck={isHardStuck}
      />

      {/* Floating chip strip — overlays the top edge of the map area
          (below the status bar + phase coach). Doesn't shrink the map
          because pointer-events:none on the wrapper passes taps through. */}
      <MobilePlayerRoster
        gameState={gameState}
        selfId={selfId}
        attackState={attackState}
        topOffsetPx={topOffset}
      />

      <div
        style={{
          position: 'absolute',
          top: topOffset,
          left: 0,
          right: 0,
          bottom: bottomOffset,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <MapView
          playerCount={playerCount}
          gameState={gameState}
          selfId={selfId}
          onSelectSource={onSelectSource}
          onSelectTarget={onSelectTarget}
          mode={inRescueDeployMode ? 'deploy' : 'attack'}
          onDeploy={onRescueDeploy}
        />
      </div>

      {showActionBar ? (
        <MobileActionBar
          isMyTurn={isMyTurn}
          currentPlayerName={currentPlayer?.name ?? null}
          attackState={attackState}
          gameState={gameState}
          sourceName={sourceName}
          targetName={targetName}
          onCommitAttack={onCommitAttack}
          onEndCombat={onEndCombat}
          onEndPhase={onEndPhase}
          onCancelSource={(id) => onSelectSource(id)}
          inRescueDeployMode={inRescueDeployMode}
          isHardStuck={isHardStuck}
          reserveCubes={reserveCubes}
        />
      ) : null}
    </>
  );
}

// -----------------------------------------------------------------------------
// MobileActionBar — persistent bottom bar with state-aware content.
// -----------------------------------------------------------------------------

interface MobileActionBarProps {
  readonly isMyTurn: boolean;
  readonly currentPlayerName: string | null;
  readonly attackState: AttackState;
  readonly gameState: GameState;
  readonly sourceName: string;
  readonly targetName: string;
  readonly onCommitAttack: (cubes: 1 | 2 | 3) => void;
  readonly onEndCombat: () => void;
  readonly onEndPhase: () => void;
  readonly onCancelSource: (id: TerritoryId) => void;
  readonly inRescueDeployMode: boolean;
  readonly isHardStuck: boolean;
  readonly reserveCubes: number;
}

function MobileActionBar({
  isMyTurn,
  currentPlayerName,
  attackState,
  gameState,
  sourceName,
  targetName,
  onCommitAttack,
  onEndCombat,
  onEndPhase,
  onCancelSource,
  inRescueDeployMode,
  isHardStuck,
  reserveCubes,
}: MobileActionBarProps) {
  // Resolve the caption + button row for the current state in one pass.
  // Keeping all states in a single switch keeps every variant rendering
  // the SAME two-row geometry: caption on top, buttons in one straight
  // line below — no per-state structural drift, no mixed alignSelf, no
  // wobbly ChalkButtons next to flat dashed buttons.
  const content: { caption: string; buttons: ReactNode } = !isMyTurn
    ? {
        caption: `WAITING FOR ${(currentPlayerName ?? 'NEXT PLAYER').toUpperCase()}…`,
        buttons: null,
      }
    : attackState.kind === 'targetSelected'
      ? {
          caption: `ATTACK ${sourceName.toUpperCase()} → ${targetName.toUpperCase()}`,
          buttons: (
            <TargetButtons
              gameState={gameState}
              onCommit={onCommitAttack}
              onCancel={onEndCombat}
            />
          ),
        }
      : attackState.kind === 'sourceSelected'
        ? {
            caption: `FROM ${sourceName.toUpperCase()} — TAP AN ADJACENT ENEMY`,
            buttons: (
              <BarButton
                variant="secondary"
                onClick={() =>
                  onCancelSource(attackState.sourceId as TerritoryId)
                }
                ariaLabel="Cancel source selection"
                grow
              >
                Cancel
              </BarButton>
            ),
          }
        : inRescueDeployMode
          ? {
              caption: `NO ATTACKS — TAP TO DEPLOY 1 CUBE · RESERVES: ${reserveCubes}`,
              buttons: (
                <BarButton
                  variant="secondary"
                  onClick={onEndPhase}
                  ariaLabel="End your turn"
                  grow
                >
                  End Turn
                </BarButton>
              ),
            }
          : isHardStuck
            ? {
                caption: 'NO POSSIBLE ATTACKS · NO RESERVES',
                buttons: (
                  <BarButton
                    variant="primary"
                    onClick={onEndPhase}
                    ariaLabel="End your turn — no actions remain"
                    grow
                  >
                    End Turn
                  </BarButton>
                ),
              }
            : {
                caption: 'TAP A TERRITORY YOU OWN TO ATTACK',
                buttons: (
                  <BarButton
                    variant="primary"
                    onClick={onEndPhase}
                    ariaLabel="End your turn"
                    grow
                  >
                    End Turn
                  </BarButton>
                ),
              };

  return (
    <div
      role="region"
      aria-label="Match actions"
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 12,
        height: MOBILE_ACTION_BAR_HEIGHT,
        background: surfaces.slate,
        borderTop: `1px solid ${surfaces.graphite}`,
        boxShadow: '0 -8px 22px rgba(0,0,0,0.42)',
        padding: '8px 12px 12px',
        paddingBottom: `calc(12px + env(safe-area-inset-bottom, 0px))`,
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}
    >
      {/* Caption row — single line of mono uppercase guidance, truncated
          on overflow so the layout below stays anchored to one baseline. */}
      <div
        style={{
          fontFamily: fontStacks.mono,
          fontSize: 10,
          letterSpacing: '0.14em',
          color: surfaces.chalk,
          opacity: 0.78,
          textAlign: 'center',
          lineHeight: 1.4,
          minHeight: 14,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {content.caption}
      </div>

      {/* Button row — every state lays its buttons at one shared 48px
          height. No rotation, no mixed border styles. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'stretch',
          gap: 8,
          flex: 1,
          minHeight: 0,
        }}
      >
        {content.buttons}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// BarButton — flat 48px button used throughout the action bar so every
// interactive element shares one baseline, one corner radius, and one
// font. Two visual variants:
//   - primary    wax-red fill (commit / end turn)
//   - secondary  transparent + dashed chalk border (cancel / fallback)
// -----------------------------------------------------------------------------

interface BarButtonProps {
  readonly variant: 'primary' | 'secondary';
  readonly onClick: () => void;
  readonly ariaLabel?: string;
  /** When true, takes flex:1 so it stretches across the bar. */
  readonly grow?: boolean;
  /** Fixed pixel width (overrides grow). Used for the ✕ cancel button. */
  readonly width?: number;
  readonly children: ReactNode;
}

function BarButton({
  variant,
  onClick,
  ariaLabel,
  grow,
  width,
  children,
}: BarButtonProps): ReactNode {
  const base = {
    height: 48,
    borderRadius: 6,
    fontFamily: fontStacks.mono,
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: '0.12em',
    textTransform: 'uppercase' as const,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    padding: '0 16px',
    flex: grow && !width ? 1 : '0 0 auto',
    width,
    minWidth: 0,
    transition: 'background-color 120ms ease, transform 120ms ease',
  };

  const variantStyle =
    variant === 'primary'
      ? {
          background: surfaces.waxRed,
          color: '#ffffff',
          border: `1px solid ${surfaces.waxRed}`,
          boxShadow: '0 1px 0 rgba(0,0,0,0.25)',
        }
      : {
          background: 'transparent',
          color: surfaces.chalk,
          border: `1px dashed ${surfaces.chalk}`,
          boxShadow: 'none',
        };

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel ?? undefined}
      style={{ ...base, ...variantStyle }}
    >
      {children}
    </button>
  );
}

// -----------------------------------------------------------------------------
// TargetButtons — cube-count picker (1/2/3) + ✕ cancel, all 48px tall.
// -----------------------------------------------------------------------------

function TargetButtons({
  gameState,
  onCommit,
  onCancel,
}: {
  readonly gameState: GameState;
  readonly onCommit: (cubes: 1 | 2 | 3) => void;
  readonly onCancel: () => void;
}): ReactNode {
  const max = maxCommittableCubes(gameState);
  const options: ReadonlyArray<1 | 2 | 3> = [1, 2, 3];
  return (
    <>
      {options
        .filter((c) => c <= max)
        .map((c) => (
          <BarButton
            key={c}
            variant="primary"
            onClick={() => onCommit(c)}
            ariaLabel={`Attack with ${c} ${c === 1 ? 'cube' : 'cubes'}`}
            grow
          >
            {c}
          </BarButton>
        ))}
      <BarButton
        variant="secondary"
        onClick={onCancel}
        ariaLabel="Cancel attack"
        width={48}
      >
        ✕
      </BarButton>
    </>
  );
}

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

function maxCommittableCubes(gameState: GameState): 1 | 2 | 3 {
  const a = gameState.attackState;
  if (a.kind !== 'targetSelected') return 1;
  const src = gameState.territories[a.sourceId];
  if (!src) return 1;
  const garrison = 1;
  const available = Math.max(0, src.cubeCount - garrison);
  if (available >= 3) return 3;
  if (available === 2) return 2;
  return 1;
}
